import { useSuspenseQuery } from "@apollo/client";
import { Grid, GridProps, Typography } from "@mui/material";
import { amber, grey } from "@mui/material/colors";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import DonutChart from "components/Dashboard/DonutChart";
import { gql } from "generated-graphql";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useChemicalOverviewState } from "../ChemicalOverviewContext";
import { report } from "process";

const DAYS_REMAINING_COLORS = {
  background: "rgba(33, 150, 243, 0.04)",
  text: "rgba(0, 0, 0, 0.6)",
  numberText: "rgba(0, 0, 0, 0.87)",
};

const MY_PROGRESS_QUERY = gql(`
  query MyProgress($tenantId: ID!, $filter: ChemicalOverviewFilter) {
    chemicalsOverviewMyProgress(tenantId: $tenantId, filter: $filter) {
      reportsRemaining
      reportsTotal
      dueDate
    }
  }
`);

// Helper function to get debug parameters from URL
function getDebugParams() {
  if (typeof window === "undefined") {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  return {
    debugDate: params.get("debugDate"),
    debugReportingYear: params.get("debugReportingYear"),
    debugReportsTotal: params.get("debugReportsTotal"),
    debugReportsRemaining: params.get("debugReportsRemaining"),
  };
}

export function MyProgressTile(props: GridProps) {
  return (
    <DashboardTile {...props}>
      <MyProgressContent />
    </DashboardTile>
  );
}

function MyProgressContent() {
  const { tenantId } = useTenant();
  const {
    chemicalOverviewState: {
      deferredFilters: { assignedToMe, facilityTags },
    },
  } = useChemicalOverviewState();

  const { data } = useSuspenseQuery(MY_PROGRESS_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: { assignedToMe, tagNames: facilityTags },
    },
    fetchPolicy: "cache-and-network",
  });

  const myProgress = data?.chemicalsOverviewMyProgress;
  const debugParams = getDebugParams();

  // Apply debug overrides
  const debuggedProgress = myProgress
    ? {
        ...myProgress,
        reportsTotal: debugParams.debugReportsTotal
          ? parseInt(debugParams.debugReportsTotal, 10)
          : myProgress.reportsTotal,
        reportsRemaining: debugParams.debugReportsRemaining
          ? parseInt(debugParams.debugReportsRemaining, 10)
          : myProgress.reportsRemaining,
      }
    : null;

  // Calculate due date with potential reporting year override
  let dueDate = myProgress ? DateTime.fromISO(myProgress.dueDate) : null;
  if (dueDate && debugParams.debugReportingYear) {
    const debugYear = parseInt(debugParams.debugReportingYear, 10);
    dueDate = DateTime.local(debugYear + 1, 2, 15); // Due date is Feb 15 of (reporting year + 1)
  }

  // Use debug date if provided
  const now = debugParams.debugDate
    ? DateTime.fromISO(debugParams.debugDate)
    : DateTime.now();

  const daysRemaining = dueDate
    ? Math.max(0, Math.ceil(dueDate.diffNow("days").days + 1)) // Ensure never negative
    : null;

  // Override days remaining if using debug date
  const actualDaysRemaining =
    debugParams.debugDate && dueDate
      ? Math.max(0, Math.ceil(dueDate.diff(now, "days").days + 1))
      : daysRemaining;

  // Determine if all reports are submitted
  const allReportsSubmitted =
    debuggedProgress?.reportsRemaining === 0 &&
    debuggedProgress?.reportsTotal > 0;

  return (
    <>
      <DashboardTile.Header
        title="Progress"
        infoTooltip={<MyProgressTooltip />}
      ></DashboardTile.Header>
      <DashboardTile.ContentArea justifyContent={"space-evenly"}>
        {allReportsSubmitted && (
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.875rem",
              width: "100%",
              padding: "1rem",
              mb: 2,
              backgroundColor: "rgba(33, 150, 243, 0.04)",
              borderRadius: "4px",
            }}
          >
            🎉 Congratulations! You have submitted all reports to Encamp!
          </Typography>
        )}
        {debuggedProgress && (
          <ReportsRemainingDonutChart
            reportsRemaining={debuggedProgress.reportsRemaining}
            reportsTotal={debuggedProgress.reportsTotal}
            allReportsSubmitted={allReportsSubmitted}
          />
        )}
        {typeof actualDaysRemaining === "number" && dueDate && (
          <DaysRemainingCard
            daysRemaining={actualDaysRemaining}
            dueDate={dueDate}
          />
        )}
      </DashboardTile.ContentArea>
    </>
  );
}

function MyProgressTooltip() {
  return (
    <Typography variant="caption">
      <Typography component="span" variant="caption" fontWeight={800}>
        Progress
      </Typography>{" "}
      displays how many reports you still need to verify and submit to Encamp,
      along with how many days are left until the submission deadline.
    </Typography>
  );
}

export function getProgressDonutData({
  reportsRemaining,
  reportsTotal,
}: {
  reportsRemaining: number;
  reportsTotal: number;
}) {
  const submitted = reportsTotal - reportsRemaining;
  return {
    seriesData: [
      // The filled segment
      {
        id: "submitted",
        color: amber[300],
        value: submitted,
      },
      // The empty segment
      {
        id: "none",
        color: grey[200],
        value: reportsRemaining,
      },
    ],
    label: `${submitted}`,
    subLabel: `of ${reportsTotal.toLocaleString()}`,
  };
}

function ReportsRemainingDonutChart({
  reportsRemaining,
  reportsTotal,
  allReportsSubmitted,
}: {
  reportsRemaining: number;
  reportsTotal: number;
  allReportsSubmitted: boolean;
}) {
  const { drillDown } = useChemicalOverviewState();

  const { seriesData, label, subLabel } = getProgressDonutData({
    reportsRemaining,
    reportsTotal,
  });

  return (
    <Grid
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Typography
        component="p"
        variant="caption"
        sx={{ mb: 2, fontSize: "0.875rem" }}
      >
        Reports submitted to Encamp
      </Typography>
      <DonutChart
        seriesData={seriesData}
        seriesOverrides={{
          innerRadius: 60,
          outerRadius: 110,
          highlightScope: { highlight: "none" },
        }}
        label={label}
        subLabel={subLabel}
        containerHeight="14rem"
        showLegend={false}
        onItemClick={(event, pieItemIdentifier, item) => {
          if (!allReportsSubmitted) {
            drillDown((f) => ({
              ...f,
              organizationStatus:
                item.id === "verified"
                  ? "VERIFIED"
                  : "!VERIFIED|!NOT_REPORTING",
            }));
          }
        }}
      />
    </Grid>
  );
}

function DaysRemainingCard({
  daysRemaining,
  dueDate,
}: {
  daysRemaining: number;
  dueDate: DateTime;
}) {
  return (
    <Grid
      sx={{
        backgroundColor: DAYS_REMAINING_COLORS.background,
        borderRadius: "4px",
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "1rem",
        width: "100%",
        alignSelf: "stretch",
        marginTop: "2rem",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontFamily: "Roboto",
          fontWeight: 400,
          fontSize: "1rem",
          color: DAYS_REMAINING_COLORS.text,
          textAlign: "left",
        }}
      >
        Submit reports to Encamp by February 15th, {dueDate.year}
      </Typography>
      <Grid
        container
        sx={{
          justifyContent: "flex-end",
          alignItems: "baseline",
          gap: "8px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: "2.125rem",
            color: DAYS_REMAINING_COLORS.numberText,
            textAlign: "center",
          }}
        >
          {daysRemaining}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: "0.75rem",
            color: DAYS_REMAINING_COLORS.text,
            textAlign: "center",
          }}
        >
          days remaining
        </Typography>
      </Grid>
    </Grid>
  );
}
